import { TFunction } from 'i18next';
import moment from 'moment';
import * as Yup from 'yup';

import { TLanguage } from 'config/tenancy';
import { getCardFlagFrom } from 'shared/domain/helpers';
import { REGEXES } from 'shared/presentation/constants';
import { validateCardExpiryDate } from 'shared/presentation/helpers';

import { DOCUMENT_VALIDATORS } from './validators';

type TShapeMessages = { invalid: string; required: string };

export const DocumentShape = (lng: TLanguage, messages: TShapeMessages) =>
  Yup.string()
    .test('document', messages.invalid, (document = '') =>
      DOCUMENT_VALIDATORS[lng](document),
    )
    .required(messages.required);

export const BirthdateShape = (messages: TShapeMessages) =>
  Yup.string()
    .matches(REGEXES.DATE, messages.invalid)
    .required(messages.required)
    .test('Birthdate', messages.invalid, value => {
      if (!value) return false;

      const [days, month, year] = value.split('/');

      if (!days || !month || !year) return false;

      const diffInDays = moment(
        new Date(Number(year), Number(month) - 1, Number(days)),
      ).diff(moment(), 'days');

      if (isNaN(diffInDays)) return false;

      return diffInDays <= 0;
    });

const CARD_NAME_LENGTH = { MIN: 2, MAX: 26 };
const CVC_LENGTH = { MIN: 3, MAX: 4 };

export const CARD_SCHEMAS = {
  NAME: (t: TFunction<'shared'>) =>
    Yup.string()
      .min(
        CARD_NAME_LENGTH.MIN,
        t('Card name can not have less than {{characters}} characters', {
          characters: CARD_NAME_LENGTH.MIN,
        }),
      )
      .max(
        CARD_NAME_LENGTH.MAX,
        t('Card name can not have more than {{characters}} characters', {
          characters: CARD_NAME_LENGTH.MAX,
        }),
      )
      .matches(REGEXES.FULLNAME, t('Please, inform a valid name'))
      .required(t('Inform the name printed on the card')),
  NUMBER: (t: TFunction<'shared'>) =>
    Yup.string()
      .test('Card flag', t('Unrecognized flag'), value => {
        if (!value) return false;

        const flag = getCardFlagFrom(value);
        return flag !== undefined;
      })
      .required(t('Inform the card number')),
  EXPIRY: (t: TFunction<'shared'>) =>
    Yup.string()
      .matches(REGEXES.EXPIRY_DATE, t('It must be a valid date'))
      .required(t('Inform the expiry date'))
      .test('Expiration date', t('It must be a valid date'), value => {
        if (!value) return false;

        return validateCardExpiryDate(value);
      }),
  CVC: (t: TFunction<'shared'>) =>
    Yup.string()
      .min(
        CVC_LENGTH.MIN,
        t('It must have at least {{count}} digits', { count: CVC_LENGTH.MIN }),
      )
      .max(
        CVC_LENGTH.MAX,
        t('It must have {{count}} digits maximum', { count: CVC_LENGTH.MAX }),
      )
      .matches(/^[0-9]+$/, 'It should contain only numbers')
      .required(t("Inform the card's cvc")),
  INSTALLMENTS: (t: TFunction<'shared'>) =>
    Yup.string().required(t('Inform the number of installments')),
};
